<script setup>
import axios from 'axios'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import { onMounted, reactive } from 'vue'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import isBetween from 'dayjs/plugin/isBetween'
import Calendar from 'primevue/calendar'
import format34 from '@/helpers/format34'
import { formatDatetime } from '../functions/format_works.js'

dayjs.extend(updateLocale)
dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.extend(isBetween)
dayjs.extend(format34)
dayjs.updateLocale('en', {
  weekStart: 1,
  weekdaysShort: ['日', '月', '火', '水', '木', '金', '土']
})
const props = defineProps({
  agent: {
    type: Object,
    required: true
  },
  creators: {
    type: Object,
    required: true
  },
  shiftOptions: {
    type: Array,
    required: true
  }
})

const data = reactive({
  fetched: {},
  creators: {},
  targetWorks: [],
  isLoading: false,
  isEmpty: false,
  hasLockedWork: false,
  displayIndexDate: new Date(),
  displayedStatus: 'weekly'
})
const dateData = reactive({
  startOfCalendar: {},
  endOfCalendar: {},
  displayedStartOfCalendar: {},
  displayedEndOfCalendar: {},
  displayDates: []
})

// 月初めに飛ぶ
const setCalendarData = () => {
  dateData.startOfCalendar = dayjs(data.displayIndexDate)
    .startOf('month')
    .startOf('week')
  dateData.endOfCalendar = dayjs(data.displayIndexDate)
    .endOf('month')
    .add(1, 'w')
    .startOf('week')
  dateData.indexOfCalendar = dayjs().startOf('week')
  dateData.displayedStartOfCalendar = dayjs(data.displayIndexDate)
    .startOf('month')
    .startOf('week')
  dateData.displayedEndOfCalendar = dayjs(data.displayIndexDate)
    .startOf('month')
    .add(1, 'w')
    .startOf('week')
}
// 初回
const setData = () => {
  data.creators = props.creators
  data.isEmpty = props.creators['first_date'] === null
  dateData.startOfCalendar = dayjs().startOf('week')
  dateData.endOfCalendar = dayjs().endOf('month').add(1, 'w').startOf('week')
  dateData.displayedStartOfCalendar = dayjs().startOf('week')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
}
const fetchWorks = async () => {
  let result = await axios.get(
    `/agents/${props.agent.id}/works.json\
      ?start_date=${dateData.startOfCalendar.format('YYYY-MM-DD')}\
      &end_date=${dateData.endOfCalendar.format('YYYY-MM-DD')}`
  )
  data.fetched = result.data
}

const buildTargetWorks = () => {
  const result = []
  data.fetched.creator_works.forEach((creator_work) => {
    const display_works = []
    let indexDay = dateData.startOfCalendar.clone()
    while (indexDay.isSameOrBefore(dateData.endOfCalendar)) {
      if (
        creator_work.works.find(
          (work) => work.date === indexDay.format('YYYY-MM-DD')
        )
      )
        display_works.push(
          creator_work.works.find(
            (work) => work.date === indexDay.format('YYYY-MM-DD')
          )
        )
      else {
        display_works.push({
          date: indexDay.format('YYYY-MM-DD'),
          status: 'adjusting',
          offer_id: null,
          offer_note: '',
          entry_id: null,
          entry_note: '',
          shift_time_type_id: null,
          imported_at: null,
          reservations: []
        })
      }
      indexDay = indexDay.add(1, 'day')
    }
    result.push({
      creator_id: creator_work.creator_id,
      creator_url: `/creators/${creator_work.creator_id}/schedules`,
      ignore_expiration: creator_work.ignore_expiration,
      creator_name: creator_work.name,
      is_primary_department: creator_work.is_primary_department,
      works: display_works
    })
  })
  return result
}
const buildDisplayWorks = () => {
  dateData.displayDates = []
  let indexDay = dateData.displayedStartOfCalendar.clone()
  while (indexDay.isSameOrBefore(dateData.displayedEndOfCalendar)) {
    dateData.displayDates.push(indexDay.format('YYYY-MM-DD'))
    indexDay = indexDay.add(1, 'day')
  }
}
onMounted(async () => {
  data.isLoading = true
  await setData()
  await fetchWorks()
  data.targetWorks = buildTargetWorks()
  await buildDisplayWorks()
  data.isLoading = false
})
const isDisabledPreviousButton = () =>
  dayjs(dateData.displayDates[0]) <= dateData.startOfCalendar
const isDisabledNextButton = () =>
  dayjs(dateData.displayDates.slice(-1)[0]) >= dateData.endOfCalendar
const setPreviousDisplayWorks = () => {
  dateData.displayedStartOfCalendar =
    dateData.displayedStartOfCalendar.subtract(7, 'day')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
const setNextDisplayWorks = () => {
  dateData.displayedStartOfCalendar = dateData.displayedStartOfCalendar.add(
    7,
    'day'
  )
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
// "初週"ボタンアクション
const setInitialDisplayWorks = () => {
  dateData.displayedStartOfCalendar = dateData.startOfCalendar
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
const filterWorks = (works) =>
  works.filter(
    (work) =>
      dayjs(work.date) >= dateData.displayedStartOfCalendar &&
      dayjs(work.date) <= dateData.displayedEndOfCalendar
  )

const dayBgColor = (day) => {
  if (dayjs(day.date).isBefore(dayjs(), 'day')) {
    switch (day.status) {
      case 'adjusting':
        return 'bg-[#e4e2d0]'
      case 'monthly_fixed':
        return 'bg-[#d7dde5]'
      case 'weekly_fixed':
        return 'bg-[#d8e3db]'
      default:
        return 'bg-gray-50 text-gray-500'
    }
  }

  switch (day.status) {
    case 'adjusting':
      return 'bg-yellow-50'
    case 'monthly_fixed':
      return 'bg-blue-50'
    case 'weekly_fixed':
      return 'bg-green-50'
    default:
      return 'bg-gray-50 text-gray-500'
  }
}
const shiftBgColor = (day, type_id) => {
  if (day[type_id] == null) {
    return 'bg-gray-100'
  }
  if (type_id === 'offer_id') return 'bg-orange-300'
  if (type_id === 'entry_id') return 'bg-red-300'
}
const displayShift = (shiftId) => {
  const shift = props.shiftOptions.find(({ id }) => id === shiftId)
  if (shift) return shift.display_name

  return null
}
const updateCalendar = async () => {
  data.isLoading = true
  await setCalendarData()
  await fetchWorks()
  data.targetWorks = buildTargetWorks()
  buildDisplayWorks()
  data.isLoading = false
}
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <div class="relative pt-5">
    <div class="pb-6">
      <div class="mx-auto px-4 sm:px-6 md:px-8">
        <div v-if="data.isEmpty" class="lg:flex lg:h-full lg:flex-col">
          <header class="px-1 py-3">
            <div class="mb-8 flex items-center justify-between">
              <div class="text-2xl font-semibold text-gray-800">
                {{ props.agent.user.name }} 管理クリエイター一覧
              </div>
            </div>
          </header>
          <div class="mt-10 grid grid-cols-12">
            <div class="col-span-8 col-start-3">
              <div class="overflow-hidden rounded-lg bg-white px-6 py-5 shadow">
                <div
                  class="flex justify-center font-medium leading-6 text-gray-700"
                >
                  表示可能なスケジュールはございません。
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="lg:flex lg:h-full lg:flex-col">
          <header class="px-1 py-3">
            <div class="lg:flex lg:items-center lg:justify-between">
              <div class="min-w-0 flex-1">
                <div class="mb-4 flex flex-row flex-wrap items-center gap-4">
                  <div class="text-2xl font-semibold text-gray-800">
                    {{ props.agent.user.name }} 管理クリエイター一覧
                  </div>
                  <div>
                    <Calendar
                      v-model="data.displayIndexDate"
                      input-id="monthpicker"
                      view="month"
                      date-format="yy年 m月"
                      class="w-full"
                      @date-select="updateCalendar()"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="lg:flex lg:justify-between">
              <div
                class="sm:flex md:ml-3 md:inline-block md:items-center lg:flex lg:items-center"
              >
                <div class="flex items-center">
                  <div
                    class="mr-2 flex items-center rounded-md shadow-sm md:items-stretch lg:h-8"
                  >
                    <button
                      class="flex items-center justify-center rounded-l-md border border-r-0 border-gray-300 bg-white py-2 pr-4 pl-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                      :disabled="isDisabledPreviousButton()"
                      @click="setPreviousDisplayWorks"
                    >
                      <span class="sr-only">Previous month</span>
                      <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                    <button
                      class="hidden whitespace-nowrap border-t border-b border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:relative md:block"
                      @click="setInitialDisplayWorks"
                    >
                      初週
                    </button>
                    <span
                      class="relative -mx-px h-5 w-px bg-gray-300 md:hidden"
                    />
                    <button
                      :disabled="isDisabledNextButton()"
                      class="flex items-center justify-center rounded-r-md border border-l-0 border-gray-300 bg-white py-2 pr-3 pl-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                      @click="setNextDisplayWorks"
                    >
                      <span class="sr-only">Next month</span>
                      <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div
                  class="hidden lg:ml-4 lg:flex lg:h-6 lg:w-px lg:bg-gray-300"
                />
                <div
                  class="flex flex-wrap items-center whitespace-nowrap text-xs md:flex md:items-center"
                >
                  <div class="py-3">
                    <div
                      class="mr-2 ml-4 inline-block h-3 w-3 rounded-full bg-orange-300"
                    ></div>
                    <span class="text-gray-700">Lab.提案</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="mr-2 ml-5 inline-block h-3 w-3 rounded-full bg-red-300"
                    ></div>
                    <span class="text-gray-700">クリエイター提出</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="mr-2 ml-5 inline-block h-3 w-3 rounded-full bg-blue-300"
                    ></div>
                    <span class="text-gray-700">月間確定</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="mr-2 ml-5 inline-block h-3 w-3 rounded-full bg-green-300"
                    ></div>
                    <span class="text-gray-700">週間確定</span>
                  </div>
                  <div
                    class="ml-5 h-8 w-16 border border-gray-300 bg-yellow-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >調整中</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-blue-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >月確</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-green-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >週確</span
                    >
                  </div>
                </div>
              </div>
              <div
                class="flex-row sm:flex md:ml-3 md:inline-block md:items-center lg:flex lg:items-center"
              >
                <div class="lg:flex lg:items-center">
                  <input
                    id="weekly"
                    v-model="data.displayedStatus"
                    class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                    type="radio"
                    value="weekly"
                  />
                  <label class="mr-4 text-gray-700">週間確定を表示</label>
                  <input
                    id="monthly"
                    v-model="data.displayedStatus"
                    class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                    type="radio"
                    value="monthly"
                  />
                  <label class="mr-4 text-gray-700">月間確定を表示</label>
                  <input
                    id="adjusting"
                    v-model="data.displayedStatus"
                    class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                    type="radio"
                    value="adjusting"
                  />
                  <label class="mr-4 text-gray-700">調整中を表示</label>
                </div>
              </div>
            </div>
          </header>
          <div
            class="shadow ring-1 ring-gray-300 ring-opacity-80 lg:flex lg:flex-auto lg:flex-col"
          >
            <div
              class="sr-only grid grid-cols-9 gap-px border-gray-300 border-opacity-80 bg-gray-300 bg-opacity-80 text-center text-xs font-semibold leading-6 text-gray-700 lg:not-sr-only lg:flex-none"
            >
              <div class="bg-white py-2" />
              <div class="bg-white py-2">月</div>
              <div class="bg-white py-2">火</div>
              <div class="bg-white py-2">水</div>
              <div class="bg-white py-2">木</div>
              <div class="bg-white py-2">金</div>
              <div class="bg-white py-2 text-blue-400">土</div>
              <div class="bg-white py-2 text-red-400">日</div>
              <div class="bg-white py-2">月</div>
            </div>
            <!-- カレンダー内側 -->
            <div
              v-for="creator_works in data.targetWorks"
              :key="creator_works.creator_name"
            >
              <div
                class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto"
              >
                <div class="w-full border-t lg:grid lg:grid-cols-9 lg:gap-px">
                  <div
                    class="flex flex-col items-center justify-center bg-white p-4"
                  >
                    <div class="text-lg">
                      <label for="email-notification">
                        <a :href="creator_works.creator_url">
                          <span class="underline hover:text-gray-500">{{
                            creator_works.creator_name
                          }}</span>
                        </a>
                      </label>
                    </div>
                  </div>
                  <div
                    v-for="day in filterWorks(creator_works.works)"
                    :key="day.date"
                    :class="[
                      dayBgColor(day),
                      'relative flex py-2 px-3 lg:block'
                    ]"
                  >
                    <div
                      class="flex flex-col pr-3 lg:flex-row lg:justify-between lg:pr-0"
                    >
                      <time
                        :datetime="day.date"
                        :class="
                          dayjs(day.date).isToday()
                            ? 'flex h-6 w-6 items-center justify-center rounded-full bg-yellow-500 p-4 font-bold text-white'
                            : 'font-bold'
                        "
                      >
                        {{ dayjs(day.date).format('M/D') }}
                      </time>
                      <div
                        class="not-sr-only lg:sr-only"
                        :class="{
                          'text-blue-400': dayjs(day.date).day() === 6,
                          'text-red-400': dayjs(day.date).day() === 0
                        }"
                      >
                        {{ dayjs(day.date).format('ddd') }}
                      </div>
                      <!--履歴ボタン-->
                      <WorkHistory :day-id="day.id" />
                    </div>
                    <!--カレンダー1日分-->
                    <!--週間確定後-->
                    <div
                      v-if="
                        data.displayedStatus === 'weekly' &&
                        day.status === 'weekly_fixed'
                      "
                    >
                      <div v-if="day.reservations?.length !== 0">
                        <div
                          v-for="reservation in day.reservations"
                          :key="reservation.id"
                        >
                          <div class="my-2 flex flex-col">
                            <div
                              class="m-1 inline-flex h-8 w-36 flex-none items-center justify-center rounded-md bg-green-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                            >
                              {{
                                formatDatetime(
                                  dayjs(reservation.start_time),
                                  dayjs(reservation.end_time)
                                )
                              }}
                            </div>
                            <div class="flex flex-col pl-2.5 text-sm">
                              <div
                                v-if="
                                  reservation.studio_name ||
                                  reservation.resources_name
                                "
                              >
                                【{{ reservation.studio_name
                                }}{{ reservation.resources_name }}】
                              </div>
                              <div
                                v-if="reservation.project_name"
                                class="font-semibold"
                              >
                                {{ reservation.project_name }}
                              </div>
                              <div v-if="reservation.reservation_name">
                                {{ reservation.reservation_name }}
                              </div>
                              <div v-if="reservation.project_customer">
                                （{{ reservation.project_customer }}）
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <div class="h-32" />
                      </div>
                    </div>
                    <!--月間確定-->
                    <div
                      v-else-if="
                        (data.displayedStatus === 'monthly' ||
                          data.displayedStatus === 'weekly') &&
                        (day.status === 'monthly_fixed' ||
                          day.status === 'weekly_fixed')
                      "
                    >
                      <div v-if="day.shift_time_type_id">
                        <div class="my-2 flex gap-x-2">
                          <div
                            class="inline-flex h-28 w-12 flex-none items-center rounded-md bg-blue-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                          >
                            {{ displayShift(day.shift_time_type_id) }}
                          </div>
                          <div class="flex items-center font-semibold">
                            {{ day.name }}
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <div class="my-2 flex gap-x-2">
                          <!-- 月間確定後の予定が空白かつ追加作業日が存在する時 -->
                          <div
                            v-if="day.additional_entry_id"
                            class="flex flex-col"
                          >
                            <div
                              class="my-2 inline-flex h-12 w-12 flex-none items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-semibold text-white"
                            />
                            <!-- クリエイター追加作業希望日-->
                            <div class="flex items-center">
                              <div
                                class="my-2 inline-flex h-12 w-12 flex-none items-center rounded-md bg-red-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                              >
                                {{ displayShift(day.additional_entry_id) }}
                              </div>
                              <div class="ml-2">
                                {{ day.additional_entry_note }}
                              </div>
                            </div>
                          </div>
                          <!-- 月間確定後の予定が空白の時 -->
                          <div
                            v-else
                            class="inline-flex h-28 w-12 flex-none items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-semibold text-white"
                          ></div>
                        </div>
                      </div>
                    </div>
                    <!--調整中-->
                    <div v-else>
                      <div class="my-2 flex gap-x-2">
                        <!--Lab依頼-->
                        <div
                          :class="[
                            shiftBgColor(day, 'offer_id'),
                            'inline-flex h-12 w-12 flex-none items-center rounded-md px-2.5 py-0.5 text-sm font-semibold text-white'
                          ]"
                        >
                          {{ displayShift(day.offer_id) }}
                        </div>
                        <!--  Labメモ -->
                        <div class="editable-text">
                          {{ day.offer_note }}
                        </div>
                      </div>
                      <div class="my-2 flex gap-x-2">
                        <!-- クリエイター希望-->
                        <div
                          :class="[
                            shiftBgColor(day, 'entry_id'),
                            'inline-flex h-12 w-12 flex-none items-center rounded-md px-2.5 py-0.5 text-sm font-semibold text-white'
                          ]"
                        >
                          {{ displayShift(day.entry_id) }}
                        </div>
                        <!-- クリエイターメモ -->
                        <div class="editable-text">
                          {{ day.entry_note }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-button-lab.p-selectbutton) {
  .p-button.p-highlight {
    background: #ef8e58;
    border-color: #ef8e58;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #ef8e58,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }
}

:deep(.p-button-creator.p-selectbutton) {
  .p-button.p-highlight {
    background: #e66a6a;
    border-color: #e66a6a;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #e66a6a,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }
}

:deep(.p-button-history.p-button.p-button-icon-only.p-button-rounded) {
  height: 1.5rem;
  width: 1.5rem;
}
</style>
